/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { SelectField, SelectFieldOption } from '@squiz/sds';
import React, { forwardRef, Ref, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CDP_STRINGS } from '../../../../Constants/Strings.constants';
import { SEGMENT_BUILDER_DATA_IDS, SEGMENT_BUILDER_FORM_SELECT_IDS } from '../../SegmentBuilder.constants';
import { OPERATOR_OPTIONS, OPERATOR_TYPE_MAP } from './SelectRulesetOperator.constants';
import { RulesetOperatorTypeProps, SelectRulesetOperatorProps } from './SelectRulesetOperator.props';

/**
 * The logical operator select field for rulesets.
 * @param {SelectRulesetOperatorProps} props Props passed to `<SelectRulesetOperator />`.
 * @returns The `<SelectRulesetOperator />` component.
 */
export const SelectRulesetOperator = forwardRef(
  (props: SelectRulesetOperatorProps, ref: Ref<HTMLInputElement>): JSX.Element => {
    const { preselectedOperator, callbackFunction } = props;
    const { t } = useTranslation();

    // Labels
    const labels = useMemo(() => {
      return {
        inputLabel: t(CDP_STRINGS.rulesetOperatorLabel),
        placeholderText: t(CDP_STRINGS.rulesetOperatorPlaceholder),
      };
    }, [t]);

    const selectOptions = OPERATOR_OPTIONS;
    const defaultSelectionLabel = OPERATOR_TYPE_MAP.get(preselectedOperator ? preselectedOperator : '');

    // Use pre-selected rule as default if it's provided.
    let defaultSelection: RulesetOperatorTypeProps;
    let defaultOption = undefined;

    if (preselectedOperator) {
      defaultSelection = preselectedOperator as RulesetOperatorTypeProps;
      defaultOption = {
        key: defaultSelection,
        label: defaultSelectionLabel,
        value: defaultSelection,
      } as SelectFieldOption;
    } else {
      defaultSelection = selectOptions[0].key as RulesetOperatorTypeProps;
    }

    return (
      <>
        <SelectField
          callback={callbackFunction}
          data-id={SEGMENT_BUILDER_DATA_IDS.segmentRulesetOperator}
          defaultOption={defaultOption}
          fieldLabel={labels.inputLabel}
          id={SEGMENT_BUILDER_FORM_SELECT_IDS.rulesetOperator}
          name={SEGMENT_BUILDER_FORM_SELECT_IDS.rulesetOperator}
          options={selectOptions}
          placeholder={labels.placeholderText}
          ref={ref}
          value={defaultSelection}
        />
      </>
    );
  },
);
